import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, defaultIfEmpty, map, of } from 'rxjs';
import { ExtranetService } from '../services/extranet.service';
import { startSignIn } from '../auth/start-sign-in';
import { isAuthBypassed } from '../auth/auth-bypass';

// Not signed in -> straight to the Azure AD B2C sign-in page (no in-app login screen).
//
// Signed in -> resolve the real role server-side (GET /api/extranet/access/me), never from a B2C
// token claim: B2C has no reliable role source (a claim would be a second source of truth vs.
// property_group_member/hotel_account) — see docs/B2C-LOGIN-INTEGRATION-RUNBOOK.md and feature
// doc Open Q #22/#23. HOTEL_MANAGER and AGENCY_MANAGER get the same view.
//
// A pure platform admin (role ADMIN, no group/property) legitimately fails this manager-only gate,
// but must NOT land on /unauthorized: the default '' -> /extranet/dashboard redirect routes every
// signed-in user through this guard first, so an admin-only account (the common case in prod, where
// admins aren't dual-role like the dev test account) would otherwise be stranded on "not linked to a
// property" despite GET /me returning ADMIN. Send them to their own admin home instead — adminGuard
// on /extranet/admin then admits them. Anyone else (no membership / unknown role) -> /unauthorized.
export const hotelManagerGuard: CanActivateFn = (_route, state) => {
  const msal = inject(MsalService);
  const router = inject(Router);
  const extranetSvc = inject(ExtranetService);

  // Locally there is no MSAL account to find — the interceptor authenticates with an unsigned dev
  // JWT instead — so only the sign-in step is skipped. Role resolution below still runs against the
  // real server, exactly as in production.
  if (!isAuthBypassed()) {
    const account = msal.instance.getActiveAccount();
    // Not signed in: B2C sign-in, then back to this very page (WI 3861) — see start-sign-in.ts.
    if (!account) return startSignIn(msal, router, state.url);
  }

  return extranetSvc.getMe().pipe(
    map((me) => {
      if (me.role === 'HOTEL_MANAGER' || me.role === 'AGENCY_MANAGER') return true;
      router.navigate([me.role === 'ADMIN' ? '/extranet/admin' : '/unauthorized']);
      return false;
    }),
    catchError(() => {
      router.navigate(['/unauthorized']);
      return of(false);
    }),
    // GET /me completes without a value while the interceptor re-authenticates an expired
    // session by redirect (WI 3861): cancel this navigation, the redirect takes the page over.
    defaultIfEmpty(false),
  );
};
